import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";             // ✅ დაამატე
import { useLanguage } from "./LanguageContext";
import { api } from "../lib/http";

const API = "https://shopaholics.ge/api";

/* ========== Spinner ========== */
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;
const Loader = styled.div`
  display: flex; justify-content: center; align-items: center;
  height: 62vh;
  @media (max-width: 1200px) { height: 50vh; }
`;
const Spinner = styled.div`
  border: 4px solid rgba(0,0,0,0.1);
  border-top: 4px solid #000;
  border-radius: 50%;
  width: 40px; height: 40px;
  animation: ${spin} 1s linear infinite;
`;

/* ========== Slider UI ========== */
const SliderWrapper = styled.div`
  display: flex;
  transition: transform 0.5s ease-in-out;
  transform: translateX(${(props) => props.translate}%);
`;

// ახლის დამატება — centered container (max 1200px)
const SlideInner = styled.div`
  width: 100%;
  max-width: 1250px;
  margin: 0 auto;           /* ცენტრში გამოტანა */
  padding: 0 16px;
  position: relative;
`;

// Slide — ქვემოთ დავდექით და ცენტრში დავცენტრდით, რომ 1200px კონტეინერი იყოს შუაში
const Slide = styled.div`
  min-width: 100%;
  height: 70vh;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;     /* ქვემოთ დასმა */
  justify-content: center;   /* ცენტრში 1200px კონტეინერი */
  color: white;

  @media (max-width: 600px) { height: 55vh; }
`;

// SlideContent — უკანა ფონი ამოღებულია, მარცხენა ქვედა კუთხეში
const SlideContent = styled.div`
  text-align: left;
  color: #fff;
  background: none;          /* ❌ არანაირი ბექგრაუნდი */
  margin: 0 0 40px 0;        /* ქვემოთ 40px */
  max-width: 100%;
  text-shadow: 0 2px 6px rgba(0,0,0,.35); /* წაკითხვადობა ფონზე, სურვილისამებრ */

  h2 {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: #ffffffff; /* ან შენი ფერი */
  /* შავი კონტური ოთხივე მხარეს */
  
  @media (max-width: 600px) {
    h2 { font-size: 20px; }
  }
`;

// ღილაკი — default прозрачный, hover-ზე თბილი ეფექტი
const LinkButton = styled(Link)`
  display: inline-block;
  margin-top: 12px;
  padding: 10px 20px;
  background-color: #ffffffff;
  font-size: 1rem;
  color: #000000ff;
  text-decoration: none;
  border: 1px solid rgba(255,255,255,.9);
  border-radius: 10px;
  backdrop-filter: none;
  transition: background .2s ease, transform .15s ease;
  &:hover { background: rgba(222, 222, 222, 1); transform: translateY(-1px); }
`;


const DotsContainer = styled.div`
  display: flex; justify-content: center;
  position: absolute; bottom: 20px; width: 100%; gap: 8px;
`;
const Dot = styled.button`
  width: 12px; height: 12px; border-radius: 50%;
  background: ${(p) => (p.active ? "black" : "#ccc")};
  border: none; cursor: pointer; transition: background 0.3s;
`;

/* ========== helpers ========== */
const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.collections)) return d.collections;
  return [];
};
const collectionUrl = (c) => `/collections/${c.slug || c.id}`;
const coverOf = (c) => c.cover_image || "/images/placeholder/collection-cover.jpg";

/* ========== Component ========== */
const Slider = () => {
  const { t, lang } = useLanguage();

  const [collections, setCollections] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadingSlide, setLoadingSlide] = useState(true);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [fetching, setFetching] = useState(true);

  // Load active collections
  useEffect(() => {
    (async () => {
      try {
        setFetching(true);
        const res = await api.get(`${API}/collections/list.php`, { params: { active: 1 } });
        const arr = toArray(res).filter(Boolean);
        setCollections(arr);
      } catch {
        setCollections([]);
      } finally {
        setFetching(false);
      }
    })();
  }, []);

  // Build slides using translation keys
  const slides = useMemo(() => {
    const nameKey = t("slider.name"); // "name_ka" ან "name_en"
    const titleFrom = (c) =>
      c?.[nameKey] || c?.name_ka || c?.name_en || c?.slug || `#${c?.id}`;

    if (!Array.isArray(collections) || collections.length === 0) {
      return [
        {
          title: titleFrom({ name_ka: "ელეგანტური სუნამოები", name_en: "Elegant Perfumes" }),
          href: "/collections",
          backgroundImage: "/images/mainslide/amouage.webp",
        },
      ];
    }
    return collections.map((c) => ({
      title: titleFrom(c),
      href: collectionUrl(c),                  // ✅ href წინასწარ დათვლილი
      backgroundImage: coverOf(c),
    }));
  }, [collections, t, lang]);

  // Preload current slide image
  useEffect(() => {
    if (!slides.length) return;
    setLoadingSlide(true);

    const bg = slides[currentSlide]?.backgroundImage;
    if (!bg) { setLoadingSlide(false); return; }

    if (loadedImages.has(bg)) { setLoadingSlide(false); return; }

    const img = new Image();
    img.src = bg;
    img.onload = () => {
      setLoadedImages((prev) => new Set(prev).add(bg));
      setTimeout(() => setLoadingSlide(false), 200);
    };
    img.onerror = () => setLoadingSlide(false);
  }, [currentSlide, slides, loadedImages]);

  // Auto-advance
  useEffect(() => {
    if (!slides.length) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Reset when list or language changes
  useEffect(() => {
    setCurrentSlide(0);
    setLoadedImages(new Set());
  }, [slides.length, lang]);

  const goToSlide = (i) => setCurrentSlide(i);

  const isLoading = fetching || loadingSlide;
  const exploreLabel = t("slider.explore");

  return (
    <div style={{ position: "relative", width: "100%", overflow: "hidden" }}>
      {isLoading ? (
        <Loader><Spinner /></Loader>
      ) : (
        <>
        <SliderWrapper translate={-currentSlide * 100}>
          {slides.map((slide, idx) => (
            <Slide key={idx} style={{ backgroundImage: `url(${slide.backgroundImage})` }}>
              <SlideInner>
                <SlideContent>
                  <h2>{slide.title}</h2>
                  <LinkButton to={slide.href}>{exploreLabel}</LinkButton>
                </SlideContent>
              </SlideInner>
            </Slide>
          ))}
        </SliderWrapper>
          <DotsContainer>
            {slides.map((_, idx) => (
              <Dot key={idx} active={currentSlide === idx} onClick={() => goToSlide(idx)} />
            ))}
          </DotsContainer>
        </>
      )}
    </div>
  );
};

export default Slider;
